import Axios from 'axios';
import DataGrid, { Column } from 'devextreme-react/data-grid';
import Cookies from 'js-cookie';
import moment from 'moment';
import React, { useRef, useState } from 'react';
import { toast } from 'react-toastify';
import { Button, Card, CardBody, Col, FormGroup, Input, InputGroup, InputGroupAddon, InputGroupText, Label, Row } from 'reactstrap';
import * as XLSX from 'xlsx';

import { goRiceUrl } from '../../constant/siteConstants';

import { useAuth } from '../../auth';
import api from '../../services/api-configuration';
import { ButtonOnExporting } from '../../utils/DataGridExporter';
import useFormatMessage from '../../utils/useFormatMessage';
import Breadcrumb from '../common/breadcrumb';


// Türkçe-İngilizce kolon eşleştirmeleri
const columnMappings = {
    "Şase No": "sase",
    "Sevkiyat Türü": "shipment_type",
    "Sevkiyat Tarihi": "shipment_date",
    "Nereden": "from_location",
    "Nereye": "to_location",
    "Talep Eden": "requester",
    "Taşıyıcı Firma": "transport_company",
    "Marka": "brand",
    "Model": "model",
    "Müşteriye Fatura Edilecek Tutar (KDV Hariç)": "client_price",
    "Nakliyeciye Ödenecek Tutar (KDV Hariç)": "transport_price",
    "Sevk Sebebi": "shipment_reason"
};

// İngilizce-Türkçe kolon eşleştirmeleri (tersi)
const reverseColumnMappings = Object.entries(columnMappings).reduce((acc, [tr, en]) => {
    acc[en] = tr;
    return acc;
}, {});

const ExcelUploadTr = ({ process_type, ExcelName, Title, showBreadcrumb = true, role_name }) => {
    const { t } = useFormatMessage();
    const [isLoading, setIsLoading] = useState(false);
    const { hasRole } = useAuth(24);
    const [file, setFile] = useState(null);
    const [error, setError] = useState('');
    const fileInputRef = useRef(null);
    const [errorArray, setErrorArray] = useState([]);
    const [warningsArray, setWarningsArray] = useState([]);
    const errorGridRef = useRef(null);
    const warningsGridRef = useRef(null);

    const requiredColumnsTr = Object.keys(columnMappings);
    const requiredColumnsEn = Object.values(columnMappings);

    const handleFileChange = (e) => {
        const selectedFile = e.target.files[0];
        if (selectedFile) {
            const reader = new FileReader();
            reader.onload = async (event) => {
                try {
                    const data = new Uint8Array(event.target.result);
                    const workbook = XLSX.read(data, { type: 'array' });
                    const worksheet = workbook.Sheets[workbook.SheetNames[0]];
                    const jsonData = XLSX.utils.sheet_to_json(worksheet, {
                        raw: false,
                        dateNF: 'dd.mm.yyyy'
                    });



                    // Dosya seçildiği anda validasyon yap
                    const isValid = await validateFileColumns(selectedFile);

                    if (isValid) {
                        // Validate price fields
                        const priceValidationErrors = validatePriceFields(jsonData);

                        if (priceValidationErrors.length > 0) {
                            // Display validation errors in the grid
                            setErrorArray(priceValidationErrors.map((error, index) => ({
                                chassis_no: `Hata ${index + 1}`,
                                message: error
                            })));

                            // Show error message
                            toast.error("Excel dosyasında sayısal değerlerde hata bulundu. Lütfen kontrol ediniz.");
                            setFile(null);
                            if (fileInputRef.current) {
                                fileInputRef.current.value = '';
                            }
                        } else {
                            setFile(selectedFile);
                            setError(''); // Doğru Excel yüklendiğinde hata mesajını temizle
                            setErrorArray([]); // Hata listesini temizle
                        }
                    } else {
                        setFile(null);
                        if (fileInputRef.current) {
                            fileInputRef.current.value = '';
                        }
                    }
                } catch (error) {
                    setError("Excel dosyası okunurken bir hata oluştu!");
                    toast.error("Excel dosyası okunurken bir hata oluştu!");
                    setFile(null);
                    if (fileInputRef.current) {
                        fileInputRef.current.value = '';
                    }
                }
            };
            reader.readAsArrayBuffer(selectedFile);
        } else {
            setFile(null);
            setError('');
            setErrorArray([]);
            if (fileInputRef.current) {
                fileInputRef.current.value = '';
            }
        }
    };

    const validateFileColumns = async (file) => {
        const reader = new FileReader();
        return new Promise((resolve, reject) => {
            reader.onload = (event) => {
                const data = new Uint8Array(event.target.result);
                const workbook = XLSX.read(data, { type: 'array' });
                const worksheet = workbook.Sheets[workbook.SheetNames[0]];
                const headers = XLSX.utils.sheet_to_json(worksheet, { header: 1 })[0];

                // Kolon isimlerini normalize et
                const normalizeColumnName = (name) => String(name).toLowerCase().replace(/\s+/g, ' ').trim();

                const normalizedHeaders = headers.map(normalizeColumnName);
                const normalizedRequiredColumnsTr = requiredColumnsTr.map(normalizeColumnName);
                const normalizedRequiredColumnsEn = requiredColumnsEn.map(normalizeColumnName);

                // Eksik kolonları bul (hem Türkçe hem İngilizce kontrol)
                const missingColumns = requiredColumnsEn.filter((enCol, index) => {
                    const trCol = requiredColumnsTr[index];
                    const normalizedEnCol = normalizeColumnName(enCol);
                    const normalizedTrCol = normalizeColumnName(trCol);
                    return !normalizedHeaders.some(header =>
                        header === normalizedEnCol || header === normalizedTrCol
                    );
                });

                // Fazla kolonları bul
                const extraColumns = headers.filter((col) => {
                    const normalizedCol = normalizeColumnName(col);
                    return !normalizedRequiredColumnsTr.includes(normalizedCol) &&
                        !normalizedRequiredColumnsEn.includes(normalizedCol);
                });

                if (missingColumns.length || extraColumns.length) {
                    let errorMessage = "";

                    if (missingColumns.length > 0) {
                        const missingColumnsFormatted = missingColumns
                            .map((col, index) => {
                                const trCol = requiredColumnsTr[requiredColumnsEn.indexOf(col)];
                                return `<span style="color: red;">${trCol}</span>`;
                            })
                            .join(", ");
                        errorMessage += `Eksik kolonlar: ${missingColumnsFormatted}<br/>`;
                        errorMessage += `<small style="color: #666">Excel dosyanızda yukarıdaki kolonlar eksik. Lütfen kolon isimlerinin doğru yazıldığından emin olun.</small><br/><br/>`;
                    }

                    if (extraColumns.length > 0) {
                        const extraColumnsFormatted = extraColumns
                            .map(col => `<span style="color: red;">${col}</span>`)
                            .join(", ");
                        errorMessage += `Fazla kolonlar: ${extraColumnsFormatted}<br/>`;
                        errorMessage += `<small style="color: #666">Excel dosyanızda yukarıdaki kolonlar fazladan bulunuyor. Bu kolonlar işlenmeyecektir.</small>`;
                    }

                    setError(errorMessage);
                    resolve(false);
                } else {
                    setError(''); // Validasyon başarılı olduğunda hata mesajını temizle
                    resolve(true);
                }
            };
            reader.onerror = (error) => reject(error);
            reader.readAsArrayBuffer(file);
        });
    };

    // Function to validate price fields
    const validatePriceFields = (jsonData) => {
        const priceValidationErrors = [];

        jsonData.forEach((row, rowIndex) => {
            const clientPriceKey = "Müşteriye Fatura Edilecek Tutar (KDV Hariç)";
            const transportPriceKey = "Nakliyeciye Ödenecek Tutar (KDV Hariç)";

            // Check client_price
            if (row[clientPriceKey] !== undefined && row[clientPriceKey] !== null && row[clientPriceKey] !== '') {
                const value = String(row[clientPriceKey]);

                // Check if the value contains letters or invalid characters
                if (/[a-zA-Z]/.test(value) || /[^\d,\.\-\s]/g.test(value)) {
                    const error = `Satır ${rowIndex + 2}: "${clientPriceKey}" sütununda geçersiz sayısal değer: "${value}"`;
                    priceValidationErrors.push(error);
                }
            }

            // Check transport_price
            if (row[transportPriceKey] !== undefined && row[transportPriceKey] !== null && row[transportPriceKey] !== '') {
                const value = String(row[transportPriceKey]);

                // Check if the value contains letters or invalid characters
                if (/[a-zA-Z]/.test(value) || /[^\d,\.\-\s]/g.test(value)) {
                    const error = `Satır ${rowIndex + 2}: "${transportPriceKey}" sütununda geçersiz sayısal değer: "${value}"`;
                    priceValidationErrors.push(error);
                }
            }
        });

        return priceValidationErrors;
    };

    const transformExcelData = (data) => {
        const errors = [];
        const transformedData = data.map((row, rowIndex) => {
            const transformedRow = {};
            Object.entries(row).forEach(([key, value]) => {
                const englishKey = columnMappings[key];
                if (englishKey) {
                    if (englishKey === 'shipment_date') {
                        // Try to parse the date if it's a string
                        if (typeof value === 'string') {

                            // Boş değer kontrolü
                            if (!value || value.trim() === '') {
                                transformedRow[englishKey] = null;
                                return;
                            }
                            // Tarih formatlarını dene
                            let parsedDate = null;

                            // Önce DD.MM.YYYY formatını dene
                            parsedDate = moment(value, 'DD.MM.YYYY', true);

                            if (!parsedDate.isValid()) {
                                // Diğer formatları dene
                                parsedDate = moment(value, [
                                    'DD.MM.YYYY', 'D.M.YYYY', 'DD/MM/YYYY', 'D/M/YYYY',
                                    'YYYY-MM-DD', 'DD-MM-YYYY', 'MM/DD/YYYY', 'M/D/YYYY',
                                    'MM.DD.YYYY', 'M.D.YYYY', 'M/D/YY', 'MM/DD/YY',
                                    'D/M/YY', 'DD/MM/YY', 'D.M.YY', 'DD.MM.YY'
                                ], true);

                            }

                            if (parsedDate.isValid()) {
                                // Tarih geçerliyse, gün kaybını önlemek için parse edilen değerleri kullan
                                const day = parsedDate.date();
                                const month = parsedDate.month() + 1; // moment.js'de ay 0'dan başlar
                                const year = parsedDate.year();

                                // YYYY-MM-DD formatında string oluştur
                                const finalDate = `${year}-${month.toString().padStart(2, '0')}-${day.toString().padStart(2, '0')}`;
                                transformedRow[englishKey] = finalDate;
                            } else {

                                transformedRow[englishKey] = value;
                            }
                        } else if (value instanceof Date) {
                            // Date objesi için de aynı mantığı uygula


                            // Date objesini yerel saat diliminde parse et
                            const parsedDate = moment(value).local();

                            if (parsedDate.isValid()) {
                                // Yerel saat dilimindeki değerleri kullan
                                const day = parsedDate.date();
                                const month = parsedDate.month() + 1;
                                const year = parsedDate.year();

                                const finalDate = `${year}-${month.toString().padStart(2, '0')}-${day.toString().padStart(2, '0')}`;

                                transformedRow[englishKey] = finalDate;
                            } else {
                                console.log('❌ Date objesi parse edilemedi:', value);
                                transformedRow[englishKey] = value;
                            }
                        } else {
                            transformedRow[englishKey] = value;
                        }
                    } else if (englishKey === 'client_price' || englishKey === 'transport_price') {
                        // Handle numeric values with comma as decimal separator
                        if (value !== undefined && value !== null && value !== '') {
                            // First convert to string if not already
                            const stringValue = String(value);
                            // Check if the value contains any non-digit characters other than comma and dot
                            const hasInvalidChars = /[^\d,\.\-]/g.test(stringValue);

                            if (hasInvalidChars) {
                                // Invalid characters found
                                const rowNum = rowIndex + 2; // +2 because of headers and 1-based index
                                const columnName = reverseColumnMappings[englishKey];
                                errors.push(`Satır ${rowNum}: "${columnName}" sütununda geçersiz sayısal değer: "${value}"`);
                                transformedRow[englishKey] = null;
                            } else {
                                // Try to parse as number
                                const cleanValue = stringValue.replace(/\./g, '').replace(',', '.');
                                if (!isNaN(parseFloat(cleanValue)) && isFinite(cleanValue)) {
                                    transformedRow[englishKey] = cleanValue;
                                } else {
                                    // Failed to parse as number
                                    const rowNum = rowIndex + 2;
                                    const columnName = reverseColumnMappings[englishKey];
                                    errors.push(`Satır ${rowNum}: "${columnName}" sütununda geçersiz sayısal değer: "${value}"`);
                                    transformedRow[englishKey] = null;
                                }
                            }
                        } else {
                            transformedRow[englishKey] = value;
                        }
                    } else {
                        transformedRow[englishKey] = value;
                    }
                }
            });
            return transformedRow;
        });

        return { transformedData, errors };
    };

    const handleSubmit = async () => {
        setIsLoading(true);
        document.getElementById("submit-button").disabled = true;

        if (!file) {
            toast.error("Lütfen önce bir Excel dosyası yükleyin.");
            document.getElementById("submit-button").disabled = false;
            setIsLoading(false);
            return;
        }

        const isValid = await validateFileColumns(file);
        if (!isValid) {
            document.getElementById("submit-button").disabled = false;
            setIsLoading(false);
            return;
        }

        try {
            const reader = new FileReader();
            reader.onload = async (event) => {
                const data = new Uint8Array(event.target.result);
                const workbook = XLSX.read(data, {
                    type: 'array',
                    cellText: false,
                    cellDates: true,
                    cellNF: false
                });
                const worksheet = workbook.Sheets[workbook.SheetNames[0]];
                const jsonData = XLSX.utils.sheet_to_json(worksheet, {
                    raw: false,
                    dateNF: 'dd.mm.yyyy'
                });



                // Check price fields again in case the file was changed
                const priceValidationErrors = validatePriceFields(jsonData);

                if (priceValidationErrors.length > 0) {
                    // Display validation errors in the grid
                    setErrorArray(priceValidationErrors.map((error, index) => ({
                        chassis_no: `Hata ${index + 1}`,
                        message: error
                    })));

                    // Show error message
                    toast.error("Excel dosyasında sayısal değerlerde hata bulundu. Lütfen kontrol ediniz.");
                    setIsLoading(false);
                    document.getElementById("submit-button").disabled = false;
                    return;
                }

                // Transform data to English column names
                const { transformedData, errors } = transformExcelData(jsonData);

                // Check if there are validation errors
                if (errors && errors.length > 0) {
                    // Display validation errors
                    setErrorArray(errors.map((error, index) => ({
                        chassis_no: `Hata ${index + 1}`,
                        message: error
                    })));
                    toast.error("Excel dosyasında hatalı değerler var. Lütfen kontrol ediniz.");
                    setIsLoading(false);
                    document.getElementById("submit-button").disabled = false;
                    return;
                }

                // Create a new workbook with transformed data
                const newWorkbook = XLSX.utils.book_new();
                const newWorksheet = XLSX.utils.json_to_sheet(transformedData);
                XLSX.utils.book_append_sheet(newWorkbook, newWorksheet, 'Sheet1');

                // Convert to blob
                const wbout = XLSX.write(newWorkbook, { bookType: 'xlsx', type: 'array' });
                const blob = new Blob([wbout], { type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet' });

                // Create FormData with transformed file
                const formData = new FormData();
                formData.append('file', blob, file.name);
                formData.append('process_type', process_type);
                formData.append('user_id', parseInt(Buffer.from(localStorage.getItem("employee_id"), 'base64').toString('ascii')));

                const headers = {
                    'Content-Type': 'multipart/form-data',
                    token: Cookies.get('token')
                };



                const response = await Axios.post(`${goRiceUrl}/api/vehicle/add`, formData, { headers });

                if (response.data.success) {
                    let successCount = response.data.stats.created + response.data.stats.updated;
                    let failedCount = response.data.stats.failed;

                    let message = '';
                    if (successCount > 0) {
                        message += `${successCount} adet başarılı`;
                    }
                    if (failedCount > 0) {
                        message += message ? `, ${failedCount} adet hatalı` : `${failedCount} adet hatalı`;
                    }
                    message += ' kayıt';

                    if (failedCount === 0) {
                        toast.success(successCount + " adet kayıt başarılı bir şekilde eklendi.");
                        // Başarılı kayıttan sonra listeyi güncelle
                        const listResponse = await api.post(`/getAllSpecialTransports`, {});
                        if (listResponse.data.success) {
                            // ID'ye göre sıralama yap (büyükten küçüğe)
                            const sortedData = listResponse.data.data.sort((a, b) => b.id - a.id);
                            window.dispatchEvent(new CustomEvent('refreshSpecialTransports', { detail: sortedData }));
                        }
                    } else {
                        toast.error(message);
                    }

                    // Process errors and warnings
                    const processedErrors = response.data.results
                        .filter(result => result.error)
                        .map(result => ({
                            chassis_no: result.chassis_no,
                            message: result.error
                        }));

                    const processedWarnings = response.data.results
                        .filter(result => result.warnings || result.warning)
                        .map(result => ({
                            chassis_no: result.chassis_no,
                            message: result.warnings?.join(" 🔷 ") || result.warning
                        }));

                    setErrorArray(processedErrors);
                    setWarningsArray(processedWarnings);

                } else {
                    // API call was successful but returned an error
                    if (response.data.error) {
                        // Display the specific error message returned from the server
                        toast.error("İşlem başarısız oldu: " + response.data.error);

                        // If there's a more detailed error message, display it in the error grid
                        if (typeof response.data.error === 'string') {
                            setErrorArray([{
                                chassis_no: "Sunucu Hatası",
                                message: response.data.error
                            }]);
                        }
                    } else {
                        toast.error("İşlem başarısız oldu.");
                    }
                }
            };
            reader.readAsArrayBuffer(file);

        } catch (error) {
            console.error('Error uploading file:', error);

            // Handle API errors that return error information in the response
            if (error.response && error.response.data) {
                if (error.response.data.error) {
                    // Display the specific error message from the server
                    toast.error("Dosya yüklenirken bir hata oluştu: " + error.response.data.error);

                    // Add error to error grid
                    setErrorArray([{
                        chassis_no: "Sunucu Hatası",
                        message: error.response.data.error
                    }]);
                } else if (error.response.data.message) {
                    // Alternative error message field
                    toast.error("Dosya yüklenirken bir hata oluştu: " + error.response.data.message);

                    // Add error to error grid
                    setErrorArray([{
                        chassis_no: "Sunucu Hatası",
                        message: error.response.data.message
                    }]);
                } else {
                    toast.error("Dosya yüklenirken bir hata oluştu: " + (error.message || ""));
                }
            } else {
                toast.error("Dosya yüklenirken bir hata oluştu: " + (error.message || ""));
            }
        } finally {
            setTimeout(() => {
                setIsLoading(false);
            }, 1500);
            document.getElementById("submit-button").disabled = false;
        }
    };

    const clearFile = () => {
        setFile(null);
        setError('');
        setErrorArray([]);
        setWarningsArray([]);
        if (fileInputRef.current) {
            fileInputRef.current.value = '';
        }
    };

    const downloadSampleExcel = () => {
        const worksheet = XLSX.utils.json_to_sheet([]);
        XLSX.utils.sheet_add_aoa(worksheet, [requiredColumnsTr]);
        const workbook = XLSX.utils.book_new();
        XLSX.utils.book_append_sheet(workbook, worksheet, 'Sayfa1');
        XLSX.writeFile(workbook, `${Title}.xlsx`);
    };

    const renderResultGrid = (data, ref, title, color) => {
        if (!data || data.length === 0) return null;

        return (
            <div className="space-y-4 mt-4">
                <div className="d-flex justify-content-between align-items-center">
                    <span className="font-bold" style={{ color }}>{title}:</span>
                    <Button
                        className="btn btn-success"
                        onClick={(e) => {
                            ButtonOnExporting(
                                e,
                                ref,
                                `${ExcelName} - ${title} - ${moment(new Date()).format("DD/MM/YYYY")}`,
                                "Sayfa-1"
                            );
                        }}
                    >
                        <i className="fa fa-file-excel-o"></i>
                    </Button>
                </div>
                <DataGrid
                    ref={ref}
                    dataSource={data}
                    showColumnHeaders={false}
                    filterRow={{ visible: true }}
                    showColumnLines={true}
                    showRowLines={true}
                    showBorders={true}
                >
                    <Column
                        dataField="chassis_no"
                        alignment="center"
                        selectedFilterOperation="contains"
                        width={200}
                    />
                    <Column
                        dataField="message"
                        selectedFilterOperation="contains"
                    />
                </DataGrid>
            </div>
        );
    };

    return (
        <>
            {showBreadcrumb && <Breadcrumb current={Title} />}
            <Card className="mt-1">
                <CardBody>
                    <Row className='mb-4'>
                        <Col md="6">
                        </Col>
                        <Col md="6" style={{ textAlign: "right" }}>
                            <b>
                                Yüklemek İstediğiniz Excel Dosyasını seçiniz. Excel Şablonunu
                                <span style={{
                                    color: "blue",
                                    cursor: "pointer"
                                }} onClick={downloadSampleExcel}> buradan </span>
                                indirebilirsiniz.
                            </b>
                        </Col>
                    </Row>

                    <FormGroup style={{ display: "flex" }}>
                        <Label for="excelUpload" className="font-weight-bold">
                            Excel Dosyası Yükle
                        </Label>
                        <InputGroup>
                            <Input
                                id="excelUpload"
                                type="file"
                                accept=".xlsx, .xls"
                                onChange={handleFileChange}
                                style={{ display: 'none' }}
                                ref={fileInputRef}
                                onClick={(e) => {
                                    e.target.value = null;
                                    clearFile();
                                }}
                            />
                            <Input
                                type="text"
                                placeholder="Dosya seçin..."
                                value={file ? file.name : ''}
                                readOnly
                                style={{
                                    height: "auto"
                                }}
                            />
                            <InputGroupAddon addonType="append">
                                <InputGroupText>
                                    <Label
                                        for="excelUpload"
                                        style={{ cursor: 'pointer', marginBottom: 0 }}
                                        onClick={() => {
                                            clearFile();
                                            if (fileInputRef.current) {
                                                fileInputRef.current.value = '';
                                            }
                                        }}
                                    >
                                        Seç
                                    </Label>
                                </InputGroupText>
                            </InputGroupAddon>
                        </InputGroup>
                    </FormGroup>

                    {error && (
                        <Row>
                            <Col md="12">
                                <p style={{ fontWeight: "bold", fontSize: "15px", color: "black" }}
                                    dangerouslySetInnerHTML={{ __html: error }} />
                            </Col>
                        </Row>
                    )}

                    <Row>
                        <Col md="12" style={{
                            display: "flex",
                            justifyContent: "space-between"
                        }}>
                            <Button
                                color='danger'
                                disabled={!file || isLoading}
                                onClick={clearFile}
                            >
                                Temizle
                            </Button>
                            {isLoading && (
                                <div style={{
                                    display: 'flex',
                                    alignItems: 'center',
                                    gap: '10px',
                                    color: '#007bff'
                                }}>
                                    <span
                                        className="spinner-border spinner-border-sm"
                                        role="status"
                                        aria-hidden="true"
                                    />
                                    <span style={{ fontWeight: 'bold' }}>Yükleniyor. Lütfen sayfadan çıkmadan bekleyiniz...</span>
                                </div>
                            )}
                            {hasRole(role_name) && (
                                <Button
                                    color="primary"
                                    id='submit-button'
                                    disabled={!file || isLoading}
                                    onClick={handleSubmit}
                                >
                                    Kaydet
                                </Button>
                            )}
                        </Col>
                    </Row>

                    <hr />
                    <Row>
                        <Col md="12" className='mb-2'>
                            <span style={{
                                color: "blue",
                                fontWeight: "bold"
                            }}>
                                Excel Dosyasında bulunması gereken kolonlar:
                            </span>
                        </Col>
                        <Col md="12">
                            <table className="table table-bordered">
                                <thead>
                                    <tr style={{
                                        backgroundColor: "#e2e2e2"
                                    }}>
                                        {requiredColumnsTr.map((column, index) => (
                                            <th
                                                style={{ border: "1px solid rgb(208 208 208)" }}
                                                key={index}
                                            >
                                                {column}
                                                {column === "Şase No" &&
                                                    <span style={{
                                                        color: "red",
                                                        fontWeight: "900",
                                                        fontSize: "17px"
                                                    }}>*</span>
                                                }
                                            </th>
                                        ))}
                                    </tr>
                                    <tr>
                                        {requiredColumnsTr.map((column, index) => (
                                            <td key={index}>
                                                <i>{
                                                    column === "Şase No" ? "VXKKAHPY5S6008124" :
                                                        column === "Sevkiyat Tarihi" ? "Tarih" : "Metin"}</i>
                                            </td>
                                        ))}
                                    </tr>
                                </thead>
                            </table>
                        </Col>
                    </Row>

                    <hr />

                    {renderResultGrid(errorArray, errorGridRef, "Hatalı Şase Listesi", "red")}
                    {renderResultGrid(warningsArray, warningsGridRef, "Şase Uyarı Mesajları", "#DAA520")}

                </CardBody>
            </Card>
        </>
    );
};

export default ExcelUploadTr; 